<template>
  <div
    id="revenue-table"
    class="w-full"
  >
    <div class="space-y-4">
      <div class="bg-white max-w-fit sm:rounded-lg">
        <div class="flex items-center gap-2 px-6 py-4">
          <span class="text-lg text-primary">Data</span>
          <div
            class="px-3 py-1 text-center rounded-full select-none default-badge font-inter w-fit bg-main-primary/10 text-main-primary"
          >
            <span class="block text-xs">{{ revenueTableData.length }} data</span>
          </div>
        </div>
        <table class="divide-y divide-slate-100">
          <thead class="text-xs uppercase whitespace-nowrap">
            <tr class="font-medium tracking-wide text-center text-gray-700">
              <th class="py-2 font-bold">
                Year
              </th>
              <th class="px-4 py-2">
                <div class="flex items-center gap-2 font-normal">
                  <span class="font-bold">Revenue</span>
                  <DropDownInput
                    :options="currencies"
                    :first-value="selectedCurrency"
                    classes="w-[110px]"
                    @value-updated="selectedCurrency = $event"
                  />
                </div>
              </th>
              <th class="py-4 font-bold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(_, rowIndex) in revenueTableData"
              :key="`revenue-${rowIndex}`"
              class="whitespace-nowrap"
            >
              <td class="px-2 py-1">
                <DropDownInput
                  :options="revenueYears"
                  :first-value="revenueTableData[rowIndex].year"
                  classes="w-[110px]"
                  @value-updated="revenueTableData[rowIndex].year = $event"
                />
              </td>
              <td class="px-2 py-1">
                <TextInput
                  :val="revenueTableData[rowIndex].revenue"
                  classes="w-[120px]"
                  placeholder="Required..."
                  :required="true"
                  @input="revenueTableData[rowIndex].revenue = $event"
                  @focusout="formatRevenue($event, rowIndex)"
                />
              </td>
              <div class="px-6 py-4">
                <DestructivePrimaryButton
                  :disabled="revenueTableData.length === 1
                    && !revenueTableData[rowIndex].id
                  "
                  @click="deleteRevenueRow(rowIndex)"
                >
                  Delete
                </DestructivePrimaryButton>
              </div>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="mt-4 flex items-center justify-between py-1.5 text-sm">
        <PrimaryButton @click="addRevenueRow">
          <PlusSmall custom-style="h-5 w-5" />
        </PrimaryButton>
        <div class="flex items-center gap-2">
          <GhostButton @click="$emit('goBack')">
            Cancel
          </GhostButton>
          <PrimaryButton
            :disabled="cantSaveRevenueData"
            @click="saveRevenueData"
          >
            Save
          </PrimaryButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GhgRevenueDataTable',

  emits: ['goBack'],

  data() {
    const currentYear = new Date().getFullYear()
    const years = Array.from(Array(currentYear).keys())
      .filter(year => year > 1899)
      .reverse()
    const revenueYears = [...years]
    revenueYears.unshift(new Date().getFullYear())

    return {
      revenueYears,
      revenueTableData: [
        {
          year: revenueYears[0],
          revenue: '',
        },
      ],
      firstRevenueYear: revenueYears[0],
      selectedCurrency: '',
      currencies: [],
    }
  },

  computed: {
    cantSaveRevenueData() {
      return (
        this.revenueTableData.some(({ revenue }) => !revenue)
        || !this.revenueTableData.some(datum => !datum.id)
      )
    },
  },

  mounted() {
    const cid = localStorage.getItem('cid')
    const { companies, units, emissionTracker } = this.$apis

    Promise.all([
      companies.fetchCompany(cid),
      units.listCurrencies(),
      emissionTracker.companyHistoricalRevenueData(cid),
    ]).then((responses) => {
      const [
        { data: companyData },
        { data: currenciesData },
        { data: savedRevenueData },
      ] = responses

      this.currencies = currenciesData
        .filter(({ id }) =>
          companyData.currency ? id === companyData.currency : true,
        )
        .map(({ name }) => name)
      this.selectedCurrency = this.currencies[0]

      if (savedRevenueData.length) {
        this.revenueTableData = savedRevenueData.map((item) => {
          item.revenue = thousands(item.revenue, 4)
          return item
        })
      }
    })
  },

  methods: {
    deleteRevenueRow(index) {
      if (this.revenueTableData[index].id) {
        this.$apis.emissionTracker
          .deleteHistoricalRevenueData(this.revenueTableData[index].id)
          .then(() => this.revenueTableData.splice(index, 1))
      }
      else {
        this.revenueTableData.splice(index, 1)
      }
    },
    addRevenueRow() {
      this.revenueTableData.push({
        year: this.firstRevenueYear,
        revenue: '',
      })
    },
    formatRevenue(value, index) {
      this.revenueTableData[index].revenue = thousands(undoThousands(value), 4)
    },
    saveRevenueData() {
      const data = [...this.revenueTableData]
        .filter(datum => !datum.id)
        .map((datum) => {
          datum.company = localStorage.getItem('cid')
          datum.currency = this.selectedCurrency
          datum.revenue = Number(datum.revenue.replaceAll(',', ''))
          return datum
        })

      this.$apis.emissionTracker
        .multiCreateHistoricalRevenueData(data)
        .then(() => {
          this.$toast.success(`Data saved successfully!`, {
            duration: 3000,
            position: 'top-right',
          })
          this.$emit('goBack')
        })
    },
  },
}
</script>
